/**
 * Affine transformation matrix coefficients.
 * Maps pixel coordinates (x, y) to map coordinates (lng, lat):
 *   lng = a*x + b*y + c
 *   lat = d*x + e*y + f
 */
export interface AffineMatrix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

/**
 * A single ground control point pairing image pixel coordinates
 * with geographic map coordinates.
 */
export interface GCP {
  id: string;
  /** Pixel X coordinate on the source image (origin top-left) */
  imageX: number;
  /** Pixel Y coordinate on the source image (origin top-left) */
  imageY: number;
  /** Longitude on the map, or null if not yet placed */
  mapLng: number | null;
  /** Latitude on the map, or null if not yet placed */
  mapLat: number | null;
  /** Index used to pick a color from the GCP color palette */
  colorIndex: number;
}

/**
 * Color palette for GCP markers. Wraps around when more GCPs than colors.
 */
export const GCP_COLORS = [
  "#3cb44b",
  "#ffe119",
  "#4363d8",
  "#f58231",
  "#911eb4",
  "#e6194b",
  "#46f0f0",
  "#f032e6",
  "#bcf60c",
  "#fabebe",
  "#008080",
  "#e6beff",
  "#9a6324",
  "#800000",
  "#aaffc3",
  "#808000",
];

export const getGcpColor = (colorIndex: number): string => GCP_COLORS[colorIndex % GCP_COLORS.length];

/** Current interaction mode */
export type ImageOverlayMode = "idle" | "place-image" | "place-map";

export interface ImageOverlayState {
  /** Original filename of the uploaded image (without extension) */
  imageName: string | null;
  /** Data URL of the uploaded image */
  imageUrl: string | null;
  /** Natural width of the uploaded image in pixels */
  imageWidth: number;
  /** Natural height of the uploaded image in pixels */
  imageHeight: number;
  /** All ground control points */
  gcps: GCP[];
  /** ID of the GCP currently being placed */
  activeGcpId: string | null;
  /** Current interaction mode */
  mode: ImageOverlayMode;
  /** Opacity of the rendered image on the map (0-1) */
  opacity: number;
  /** Whether the image overlay is visible on the map */
  isVisible: boolean;
  /** Computed affine transformation matrix, null if < 3 complete GCPs */
  transformMatrix: AffineMatrix | null;
}

export interface ImageOverlayActions {
  loadImage(file: File): void;
  addGcp(): void;
  removeGcp(id: string): void;
  setImagePoint(gcpId: string, x: number, y: number): void;
  setMapPoint(gcpId: string, lng: number, lat: number): void;
  startPlacingImage(gcpId: string): void;
  startPlacingMap(gcpId: string): void;
  cancelPlacing(): void;
  setOpacity(opacity: number): void;
  setVisible(visible: boolean): void;
  clearAll(): void;
}

export interface ImageOverlayContextValue extends ImageOverlayState, ImageOverlayActions {
  /** Computed image corner coordinates for Mapbox image source, or null */
  imageCorners: [[number, number], [number, number], [number, number], [number, number]] | null;
  /** Number of GCPs with both image and map coordinates set */
  completeGcpCount: number;
  /** RMS residual error, or null if no transform */
  residualError: number | null;
}
